// src/components/WhataNewsEdit.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Trash2, ChevronDown, ChevronUp, Edit2, LogOut, FileText, Newspaper, BookOpen, Lightbulb, Languages } from 'lucide-react';
import { useTheme } from './ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import BilingualNewsEditor from './BilingualNewsEditor';
import './WhataNewsEdit.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

// Ya no necesitamos servicios externos - usamos nuestro propio servidor 🎉

// Helper para headers autenticados
const getAuthHeaders = () => {
  const token = localStorage.getItem('blog_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

// Función para generar el título por defecto con la fecha actual
const getDefaultTitle = () => {
  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return `WhataNews | ${formattedDate}`;
};

// PLANTILLAS DISPONIBLES
const ARTICLE_TEMPLATES = {
  multiNews: {
    id: 'multiNews',
    name: 'Multi-Noticias (x/5)',
    description: 'Formato clásico con hasta 5 noticias cortas',
    icon: FileText,
    defaultContent: () => [{
      title: '',
      body: '',
      url: '',
      urlText: '',
      imageUrl: '',
      imageSize: 'medium',
    }]
  },
  longForm: {
    id: 'longForm',
    name: 'Artículo Largo',
    description: 'Formato de periódico para una noticia completa y detallada',
    icon: Newspaper,
    defaultContent: () => ({
      sections: [
        { type: 'paragraph', content: '' }
      ]
    })
  },
  tutorial: {
    id: 'tutorial',
    name: 'Tutorial/Guía',
    description: 'Formato paso a paso con secciones numeradas',
    icon: BookOpen,
    defaultContent: () => ({
      steps: [
        { title: '', content: '', imageUrl: '' }
      ]
    })
  },
  analysis: {
    id: 'analysis',
    name: 'Análisis/Insight',
    description: 'Formato para análisis profundo con puntos clave',
    icon: Lightbulb,
    defaultContent: () => ({
      introduction: '',
      keyPoints: [
        { title: '', content: '' }
      ],
      conclusion: ''
    })
  }
};

// ==================== COMPONENTE DE CARGA DE IMÁGENES ====================

const ImageUploader = ({ onImageUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [inputId] = useState(`image-upload-${Date.now()}-${Math.floor(Math.random() * 1000)}`);

  const uploadToOwnServer = async (file) => {
    const token = localStorage.getItem('blog_token');
    
    if (!token) {
      throw new Error('No estás autenticado. Por favor inicia sesión.');
    }

    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE}/upload/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Sesión expirada. Por favor inicia sesión nuevamente.');
      }
      const errorData = await response.json();
      throw new Error(errorData.message || 'Error al subir la imagen');
    }

    const data = await response.json();
    
    if (data.success && data.data && data.data.fullUrl) {
      return data.data.fullUrl;
    } else {
      throw new Error('Respuesta inesperada del servidor');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido (JPEG, PNG, GIF, WEBP)');
      return;
    }

    // Validar tamaño (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen debe ser menor a 5MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Crear preview local
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Subir al servidor propio
      const imageUrl = await uploadToOwnServer(file);
      
      onImageUploaded(imageUrl);
      
      // Limpiar después de 2 segundos
      setTimeout(() => {
        setPreview(null);
        setError(null);
        // Resetear el input
        e.target.value = '';
      }, 2000);

    } catch (error) {
      console.error('Error uploading image:', error);
      setError(error.message);
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handlePasteURL = () => {
    const url = prompt('Pega la URL de la imagen:', 'https://');
    if (url && url.startsWith('http')) {
      onImageUploaded(url);
      setError(null);
    } else if (url) {
      setError('Por favor ingresa una URL válida que comience con http:// o https://');
    }
  };

  return (
    <div className="image-uploader">
      <div className="upload-options">
        <input
          type="file"
          id={inputId}
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          disabled={uploading}
        />
        <label 
          htmlFor={inputId}
          className="upload-button"
        >
          {uploading ? (
            <>⏳ Subiendo...</>
          ) : (
            <>📤 Subir imagen desde PC</>
          )}
        </label>
        
        <button 
          type="button"
          className="upload-button upload-button--secondary"
          onClick={handlePasteURL}
          disabled={uploading}
        >
          🔗 Pegar URL
        </button>
      </div>

      {error && (
        <div className="upload-error">
          ⚠️ {error}
        </div>
      )}

      {preview && !error && (
        <div className="upload-preview">
          <img src={preview} alt="Preview" />
          <span>✓ Imagen subida correctamente</span>
        </div>
      )}
      
      <div className="upload-info">
        💾 Las imágenes se guardan en tu servidor (máx 5MB)
      </div>
    </div>
  );
};

// Componente de Selector de Plantilla
const TemplateSelector = ({ selectedTemplate, onSelect }) => {
  return (
    <div className="template-selector">
      <h3>Selecciona el formato del artículo</h3>
      <div className="template-grid">
        {Object.values(ARTICLE_TEMPLATES).map(template => {
          const Icon = template.icon;
          return (
            <button
              key={template.id}
              type="button"
              className={`template-card ${selectedTemplate === template.id ? 'active' : ''}`}
              onClick={() => onSelect(template.id)}
            >
              <Icon size={32} />
              <h4>{template.name}</h4>
              <p>{template.description}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Componente de Editor de SEO
const SEOEditor = ({ seoData, onChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleChange = (field, value) => {
    onChange({ ...seoData, [field]: value });
  };

  return (
    <div className="seo-editor-section">
      <button
        type="button"
        className="seo-toggle-btn"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span>SEO & Meta Tags</span>
        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {isExpanded && (
        <div className="seo-fields">
          <div className="seo-info">
            <p>Optimiza cómo se muestra tu artículo en buscadores y redes sociales. Los campos vacíos se generarán automáticamente.</p>
          </div>

          <div className="form-group">
            <label htmlFor="metaTitle">
              Meta Title
              <span className="label-hint">(Google Search)</span>
            </label>
            <input
              id="metaTitle"
              type="text"
              value={seoData.metaTitle}
              onChange={(e) => handleChange('metaTitle', e.target.value)}
              placeholder="Se generará del título del artículo"
              maxLength={70}
            />
            <span className={`char-count ${seoData.metaTitle.length > 60 ? 'warning' : ''}`}>
              {seoData.metaTitle.length}/70 caracteres {seoData.metaTitle.length > 60 && '⚠️'}
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="metaDescription">
              Meta Description
              <span className="label-hint">(Google Search)</span>
            </label>
            <textarea
              id="metaDescription"
              value={seoData.metaDescription}
              onChange={(e) => handleChange('metaDescription', e.target.value)}
              placeholder="Se generará del extracto del artículo"
              maxLength={160}
              rows={3}
            />
            <span className={`char-count ${seoData.metaDescription.length > 155 ? 'warning' : ''}`}>
              {seoData.metaDescription.length}/160 caracteres {seoData.metaDescription.length > 155 && '⚠️'}
            </span>
          </div>

          <div className="seo-divider">
            <span>Open Graph (Facebook, LinkedIn)</span>
          </div>

          <div className="form-group">
            <label htmlFor="ogTitle">OG Title</label>
            <input
              id="ogTitle"
              type="text"
              value={seoData.ogTitle}
              onChange={(e) => handleChange('ogTitle', e.target.value)}
              placeholder="Se usará el Meta Title"
              maxLength={70}
            />
            <span className="char-count">{seoData.ogTitle.length}/70</span>
          </div>

          <div className="form-group">
            <label htmlFor="ogDescription">OG Description</label>
            <textarea
              id="ogDescription"
              value={seoData.ogDescription}
              onChange={(e) => handleChange('ogDescription', e.target.value)}
              placeholder="Se usará la Meta Description"
              maxLength={200}
              rows={3}
            />
            <span className="char-count">{seoData.ogDescription.length}/200</span>
          </div>

          <div className="form-group">
            <label htmlFor="ogImage">OG Image URL</label>
            <input
              id="ogImage"
              type="url"
              value={seoData.ogImage}
              onChange={(e) => handleChange('ogImage', e.target.value)}
              placeholder="Se usará el banner del artículo"
              maxLength={500}
            />
            {seoData.ogImage && (
              <div className="og-image-preview">
                <img
                  src={seoData.ogImage}
                  alt="OG Preview"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
          </div>

          <div className="seo-divider">
            <span>Twitter Card</span>
          </div>

          <div className="form-group">
            <label htmlFor="twitterCard">Twitter Card Type</label>
            <select
              id="twitterCard"
              value={seoData.twitterCard}
              onChange={(e) => handleChange('twitterCard', e.target.value)}
            >
              <option value="summary_large_image">Summary Large Image (Recomendado)</option>
              <option value="summary">Summary</option>
              <option value="app">App</option>
              <option value="player">Player</option>
            </select>
            <span className="label-hint">
              {seoData.twitterCard === 'summary_large_image' && 'Imagen grande prominente'}
              {seoData.twitterCard === 'summary' && 'Imagen pequeña al lado'}
              {seoData.twitterCard === 'app' && 'Para aplicaciones móviles'}
              {seoData.twitterCard === 'player' && 'Para contenido multimedia'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== EDITORES POR PLANTILLA ====================

// Editor Multi-Noticias (formato original x/5)
const MultiNewsEditor = ({ content, onChange }) => {
  const handleNewsChange = (index, updatedNews) => {
    const newContent = [...content];
    newContent[index] = updatedNews;
    onChange(newContent);
  };

  const handleAddNews = () => {
    if (content.length < 5) {
      onChange([
        ...content,
        {
          title: '',
          body: '',
          url: '',
          urlText: '',
          imageUrl: '',
          imageSize: 'medium',
        },
      ]);
    }
  };

  const handleRemoveNews = (index) => {
    if (content.length > 1) {
      onChange(content.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="multi-news-editor">
      <div className="news-editor-header">
        <label>Contenido - Noticias</label>
        {content.length < 5 && (
          <button
            type="button"
            className="btn btn--add-news"
            onClick={handleAddNews}
          >
            <Plus size={16} />
            Añadir Noticia ({content.length}/5)
          </button>
        )}
      </div>

      <div className="news-items-container">
        {content.map((news, index) => (
          <NewsItemEditor
            key={index}
            news={news}
            index={index}
            onChange={handleNewsChange}
            onRemove={handleRemoveNews}
            canRemove={content.length > 1}
          />
        ))}
      </div>
    </div>
  );
};

const NewsItemEditor = ({ news, index, onChange, onRemove, canRemove }) => {
  const handleChange = (field, value) => {
    onChange(index, { ...news, [field]: value });
  };

  return (
    <div className="news-item-editor">
      <div className="news-item-header">
        <h4>Noticia {index + 1}</h4>
        {canRemove && (
          <button
            type="button"
            className="btn-remove-news"
            onClick={() => onRemove(index)}
            title="Eliminar noticia"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>

      <div className="news-item-fields">
        <div className="form-group">
          <label htmlFor={`news-title-${index}`}>Título Noticia {index + 1} *</label>
          <input
            id={`news-title-${index}`}
            type="text"
            value={news.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Título de la noticia"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor={`news-body-${index}`}>Cuerpo Noticia {index + 1} *</label>
          <textarea
            id={`news-body-${index}`}
            value={news.body}
            onChange={(e) => handleChange('body', e.target.value)}
            placeholder="Contenido de la noticia"
            rows={4}
            required
          />
        </div>

        <div className="form-group-row">
          <div className="form-group">
            <label htmlFor={`news-url-${index}`}>URL</label>
            <input
              id={`news-url-${index}`}
              type="url"
              value={news.url}
              onChange={(e) => handleChange('url', e.target.value)}
              placeholder="https://ejemplo.com"
            />
          </div>
          <div className="form-group">
            <label htmlFor={`news-url-text-${index}`}>Texto del enlace</label>
            <input
              id={`news-url-text-${index}`}
              type="text"
              value={news.urlText}
              onChange={(e) => handleChange('urlText', e.target.value)}
              placeholder="Ej: Leer más"
            />
          </div>
        </div>

        <div className="form-group-row">
          <div className="form-group">
            <label htmlFor={`news-image-${index}`}>URL Imagen</label>
            <input
              id={`news-image-${index}`}
              type="url"
              value={news.imageUrl}
              onChange={(e) => handleChange('imageUrl', e.target.value)}
              placeholder="https://ejemplo.com/imagen.jpg"
            />
          </div>
          <div className="form-group">
            <label htmlFor={`news-image-size-${index}`}>Tamaño imagen</label>
            <select
              id={`news-image-size-${index}`}
              value={news.imageSize}
              onChange={(e) => handleChange('imageSize', e.target.value)}
            >
              <option value="small">Pequeño</option>
              <option value="medium">Mediano</option>
              <option value="large">Grande</option>
              <option value="full">Ancho completo</option>
            </select>
          </div>
        </div>

        <ImageUploader onImageUploaded={(url) => handleChange('imageUrl', url)} />

        {news.imageUrl && (
          <div className={`news-image-preview news-image-preview--${news.imageSize}`}>
            <img
              src={news.imageUrl}
              alt={`Preview ${index + 1}`}
              onError={(e) => e.target.style.display = 'none'}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== EDITOR DE ARTÍCULO LARGO CON HTML COMPLETO ====================

const LongFormEditor = ({ content, onChange }) => {
  const [editorMode, setEditorMode] = useState('visual');
  const [htmlContent, setHtmlContent] = useState('');

  const sectionsToHTML = useCallback(() => {
    if (!content.sections || content.sections.length === 0) return '';

    return content.sections.map(section => {
      switch (section.type) {
        case 'paragraph':
          return `<p>${section.content}</p>`;
        case 'heading':
          return `<h2>${section.content}</h2>`;
        case 'quote':
          return `<blockquote>${section.content}${section.author ? `<cite>— ${section.author}</cite>` : ''}</blockquote>`;
        case 'image':
          return `<figure><img src="${section.imageUrl}" alt="${section.imageCaption || ''}" />${section.imageCaption ? `<figcaption>${section.imageCaption}</figcaption>` : ''}</figure>`;
        case 'list':
          const items = section.content.split('\n').filter(item => item.trim());
          return `<ul>${items.map(item => `<li>${item}</li>`).join('')}</ul>`;
        case 'table':
          return section.content;
        default:
          return '';
      }
    }).join('\n\n');
  }, [content.sections]);

  // ✅ VERSIÓN DEFINITIVA - SOPORTE COMPLETO PARA TODO HTML
  const htmlToSections = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html.trim();

    const sections = [];

    // Elementos que siempre deben procesarse como bloques independientes
    const BLOCK_ELEMENTS = [
      'P', 'DIV', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'MAIN', 'NAV', 'ASIDE',
      'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
      'BLOCKQUOTE', 'FIGURE', 'TABLE', 'HR',
      'PRE', 'ADDRESS', 'FIELDSET', 'FORM'
    ];

    // Elementos inline que se preservan dentro del contenido
    const INLINE_ELEMENTS = [
      'A', 'ABBR', 'B', 'BDI', 'BDO', 'BR', 'CITE', 'CODE', 'DATA', 'DFN',
      'EM', 'I', 'KBD', 'MARK', 'Q', 'S', 'SAMP', 'SMALL', 'SPAN', 'STRONG',
      'SUB', 'SUP', 'TIME', 'U', 'VAR', 'WBR', 'DEL', 'INS'
    ];

    // Función para procesar nodos hijos recursivamente
    const processChildren = (parentNode) => {
      const children = Array.from(parentNode.childNodes);
      let textBuffer = '';

      const flushTextBuffer = () => {
        const trimmed = textBuffer.trim();
        if (!trimmed) {
          textBuffer = '';
          return;
        }

        // Detectar si el buffer contiene listas HTML
        if (trimmed.includes('<ul') || trimmed.includes('<ol')) {
          const listDiv = document.createElement('div');
          listDiv.innerHTML = trimmed;
          
          // Extraer todas las listas
          const lists = listDiv.querySelectorAll('ul, ol');
          lists.forEach(list => {
            const items = Array.from(list.querySelectorAll(':scope > li')).map(li => li.innerHTML.trim());
            if (items.length > 0) {
              sections.push({ type: 'list', content: items.join('\n') });
            }
          });

          // Procesar texto restante que no es lista
          const remainingText = listDiv.innerText.trim();
          if (remainingText && !remainingText.match(/^[\s\n]*$/)) {
            const cleanedHTML = trimmed
              .replace(/<\/?[uo]l[^>]*>/gi, '')
              .replace(/<\/?li[^>]*>/gi, '')
              .trim();
            if (cleanedHTML) {
              sections.push({ type: 'paragraph', content: cleanedHTML });
            }
          }
        } else {
          // Es un párrafo normal con posible HTML inline
          sections.push({ type: 'paragraph', content: trimmed });
        }

        textBuffer = '';
      };

      children.forEach((node) => {
        // Nodos de texto plano
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          if (text.trim()) {
            textBuffer += text;
          }
        }
        // Nodos de elementos
        else if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName.toUpperCase();

          // Elementos de bloque - vaciar buffer y procesarlos
          if (BLOCK_ELEMENTS.includes(tagName)) {
            flushTextBuffer();
            processBlockElement(node);
          }
          // Listas - vaciar buffer y procesarlas
          else if (tagName === 'UL' || tagName === 'OL') {
            flushTextBuffer();
            const items = Array.from(node.querySelectorAll(':scope > li')).map(li => li.innerHTML.trim());
            if (items.length > 0) {
              sections.push({ type: 'list', content: items.join('\n') });
            }
          }
          // Elementos inline - agregar al buffer con su HTML completo
          else if (INLINE_ELEMENTS.includes(tagName)) {
            textBuffer += node.outerHTML;
          }
          // Elementos desconocidos - intentar preservar
          else {
            textBuffer += node.outerHTML;
          }
        }
        // Comentarios - ignorar
        else if (node.nodeType === Node.COMMENT_NODE) {
          // Ignorar comentarios
        }
      });

      // Vaciar buffer final
      flushTextBuffer();
    };

    // Función para procesar elementos de bloque
    const processBlockElement = (node) => {
      const tagName = node.tagName.toUpperCase();

      // Párrafos
      if (tagName === 'P') {
        const content = node.innerHTML.trim();
        if (content && content !== '<br>' && content !== '<br/>') {
          sections.push({ type: 'paragraph', content });
        }
      }
      // Títulos (H1-H6)
      else if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(tagName)) {
        const content = node.textContent.trim();
        if (content) {
          sections.push({ type: 'heading', content });
        }
      }
      // Citas
      else if (tagName === 'BLOCKQUOTE') {
        const cite = node.querySelector('cite');
        let content = node.innerHTML;
        let author = '';

        if (cite) {
          author = cite.textContent.replace(/^[—-]\s*/, '').trim();
          content = content.replace(/<cite[^>]*>.*?<\/cite>/gi, '').trim();
        }

        if (content) {
          sections.push({ type: 'quote', content, author });
        }
      }
      // Figuras (imágenes)
      else if (tagName === 'FIGURE') {
        const img = node.querySelector('img');
        const figcaption = node.querySelector('figcaption');

        if (img) {
          sections.push({
            type: 'image',
            imageUrl: img.src || img.getAttribute('src') || '',
            imageCaption: figcaption ? figcaption.textContent.trim() : ''
          });
        }
      }
      // Separador horizontal
      else if (tagName === 'HR') {
        sections.push({ type: 'paragraph', content: '<hr />' });
      }
      // Tablas - preservar HTML completo
      else if (tagName === 'TABLE') {
        sections.push({ type: 'table', content: node.outerHTML });
      }
      // Pre - código formateado
      else if (tagName === 'PRE') {
        sections.push({ type: 'paragraph', content: node.outerHTML });
      }
      // Contenedores genéricos - procesar sus hijos
      else if (['DIV', 'SECTION', 'ARTICLE', 'MAIN', 'HEADER', 'FOOTER', 'NAV', 'ASIDE', 'ADDRESS', 'FIELDSET', 'FORM'].includes(tagName)) {
        processChildren(node);
      }
      // Otros elementos de bloque - preservar como párrafo
      else {
        const content = node.outerHTML;
        if (content.trim()) {
          sections.push({ type: 'paragraph', content });
        }
      }
    };

    // Iniciar procesamiento
    processChildren(tempDiv);

    return sections.length > 0 ? sections : [{ type: 'paragraph', content: '' }];
  };

  useEffect(() => {
    if (editorMode === 'html') {
      setHtmlContent(sectionsToHTML());
    }
  }, [editorMode, sectionsToHTML]);

  const handleModeSwitch = (mode) => {
    if (mode === 'visual' && editorMode === 'html') {
      const newSections = htmlToSections(htmlContent);
      onChange({ ...content, sections: newSections });
    }
    setEditorMode(mode);
  };

  const handleHTMLChange = (value) => {
    setHtmlContent(value);
  };

  const applyHTMLChanges = () => {
    const newSections = htmlToSections(htmlContent);
    console.log('✅ Secciones generadas:', newSections);
    onChange({ ...content, sections: newSections });
    setEditorMode('visual');
  };

  return (
    <div className="longform-editor">
      <div className="editor-mode-selector">
        <button
          type="button"
          className={`mode-btn ${editorMode === 'visual' ? 'active' : ''}`}
          onClick={() => handleModeSwitch('visual')}
        >
          📝 Editor Visual
        </button>
        <button
          type="button"
          className={`mode-btn ${editorMode === 'html' ? 'active' : ''}`}
          onClick={() => handleModeSwitch('html')}
        >
          &lt;/&gt; Editor HTML
        </button>
      </div>

      {editorMode === 'visual' ? (
        <VisualEditor content={content} onChange={onChange} />
      ) : (
        <HTMLEditor
          htmlContent={htmlContent}
          onChange={handleHTMLChange}
          onApply={applyHTMLChanges}
        />
      )}
    </div>
  );
};

// Editor Visual con botones estilo Word
const VisualEditor = ({ content, onChange }) => {
  const [selectedSection, setSelectedSection] = useState(null);

  const handleSectionChange = (index, field, value) => {
    const newSections = [...content.sections];
    newSections[index] = { ...newSections[index], [field]: value };
    onChange({ ...content, sections: newSections });
  };

  const handleAddSection = (type) => {
    const newSection = { type, content: '', imageUrl: '', imageCaption: '' };
    onChange({ ...content, sections: [...content.sections, newSection] });
  };

  const handleRemoveSection = (index) => {
    if (content.sections.length > 1) {
      onChange({ ...content, sections: content.sections.filter((_, i) => i !== index) });
    }
  };

  const handleMoveSection = (index, direction) => {
    const newSections = [...content.sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
    onChange({ ...content, sections: newSections });
  };

  const applyFormatting = (index, format) => {
    const section = content.sections[index];
    if (section.type !== 'paragraph') return;

    const textarea = document.getElementById(`section-content-${index}`);
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = section.content.substring(start, end);

    if (!selectedText) return;

    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `<strong>${selectedText}</strong>`;
        break;
      case 'italic':
        formattedText = `<em>${selectedText}</em>`;
        break;
      case 'underline':
        formattedText = `<u>${selectedText}</u>`;
        break;
      case 'link':
        const url = prompt('Ingresa la URL:', 'https://');
        if (url) formattedText = `<a href="${url}">${selectedText}</a>`;
        break;
      case 'code':
        formattedText = `<code>${selectedText}</code>`;
        break;
      default:
        return;
    }

    const newContent =
      section.content.substring(0, start) +
      formattedText +
      section.content.substring(end);

    handleSectionChange(index, 'content', newContent);
  };

  return (
    <>
      <div className="visual-editor-toolbar">
        <label>Contenido del Artículo</label>
        <div className="toolbar-buttons">
          <button type="button" onClick={() => handleAddSection('heading')} title="Añadir Título">
            <strong>H</strong>
          </button>
          <button type="button" onClick={() => handleAddSection('paragraph')} title="Añadir Párrafo">
            <span>¶</span>
          </button>
          <button type="button" onClick={() => handleAddSection('quote')} title="Añadir Cita">
            <span>"</span>
          </button>
          <button type="button" onClick={() => handleAddSection('image')} title="Añadir Imagen">
            <span>🖼️</span>
          </button>
          <button type="button" onClick={() => handleAddSection('list')} title="Añadir Lista">
            <span>≡</span>
          </button>
          <button type="button" onClick={() => handleAddSection('table')} title="Añadir Tabla">
            <span>⊞</span>
          </button>
        </div>
      </div>

      <div className="longform-sections">
        {content.sections.map((section, index) => (
          <div
            key={index}
            className={`longform-section ${selectedSection === index ? 'selected' : ''}`}
            onClick={() => setSelectedSection(index)}
          >
            <div className="section-controls">
              <span className="section-type-badge">{section.type}</span>
              <div className="section-actions">
                {index > 0 && (
                  <button type="button" onClick={() => handleMoveSection(index, 'up')} title="Subir">
                    ↑
                  </button>
                )}
                {index < content.sections.length - 1 && (
                  <button type="button" onClick={() => handleMoveSection(index, 'down')} title="Bajar">
                    ↓
                  </button>
                )}
                {content.sections.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveSection(index)}
                    title="Eliminar"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>

            {section.type === 'paragraph' && (
              <>
                <div className="formatting-toolbar">
                  <button type="button" onClick={() => applyFormatting(index, 'bold')} title="Negrita">
                    <strong>B</strong>
                  </button>
                  <button type="button" onClick={() => applyFormatting(index, 'italic')} title="Cursiva">
                    <em>I</em>
                  </button>
                  <button type="button" onClick={() => applyFormatting(index, 'underline')} title="Subrayado">
                    <u>U</u>
                  </button>
                  <button type="button" onClick={() => applyFormatting(index, 'link')} title="Enlace">
                    🔗
                  </button>
                  <button type="button" onClick={() => applyFormatting(index, 'code')} title="Código">
                    &lt;/&gt;
                  </button>
                </div>
                
                {/* Preview del contenido con HTML renderizado */}
                {section.content && section.content.match(/<[^>]+>/g) && (
                  <div 
                    className="paragraph-preview"
                    dangerouslySetInnerHTML={{ __html: section.content }}
                  />
                )}
                
                <textarea
                  id={`section-content-${index}`}
                  value={section.content}
                  onChange={(e) => handleSectionChange(index, 'content', e.target.value)}
                  placeholder="Escribe un párrafo... Puedes usar HTML: <strong>, <em>, <a href=''>, <br>, etc."
                  rows={section.content.length > 200 ? 6 : 4}
                />
                <div className="paragraph-hint">
                  💡 El contenido HTML se renderiza automáticamente arriba. Puedes editarlo directamente aquí.
                </div>
              </>
            )}

            {section.type === 'heading' && (
              <input
                type="text"
                value={section.content}
                onChange={(e) => handleSectionChange(index, 'content', e.target.value)}
                placeholder="Escribe un título de sección..."
                className="heading-input"
              />
            )}

            {section.type === 'quote' && (
              <>
                <textarea
                  value={section.content}
                  onChange={(e) => handleSectionChange(index, 'content', e.target.value)}
                  placeholder="Escribe una cita destacada..."
                  rows={3}
                  className="quote-textarea"
                />
                <input
                  type="text"
                  value={section.author || ''}
                  onChange={(e) => handleSectionChange(index, 'author', e.target.value)}
                  placeholder="Autor de la cita (opcional)"
                  className="quote-author"
                />
              </>
            )}

            {section.type === 'image' && (
              <>
                <div className="image-input-group">
                  <input
                    type="url"
                    value={section.imageUrl}
                    onChange={(e) => handleSectionChange(index, 'imageUrl', e.target.value)}
                    placeholder="URL de la imagen o sube una desde tu PC"
                  />
                  <ImageUploader
                    onImageUploaded={(url) => handleSectionChange(index, 'imageUrl', url)}
                  />
                </div>
                <input
                  type="text"
                  value={section.imageCaption || ''}
                  onChange={(e) => handleSectionChange(index, 'imageCaption', e.target.value)}
                  placeholder="Pie de foto (opcional)"
                />
                {section.imageUrl && (
                  <div className="section-image-preview">
                    <img
                      src={section.imageUrl}
                      alt="Preview"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </>
            )}

            {section.type === 'list' && (
              <>
                {section.content && (
                  <div
                    className="list-preview"
                    dangerouslySetInnerHTML={{
                      __html: '<ul>' +
                        section.content
                          .split('\n')
                          .filter(item => item.trim())
                          .map(item => `<li>${item}</li>`)
                          .join('') +
                        '</ul>'
                    }}
                  />
                )}
                <textarea
                  value={section.content}
                  onChange={(e) => handleSectionChange(index, 'content', e.target.value)}
                  placeholder="Escribe cada ítem en una línea nueva.&#10;Puedes usar HTML inline: <strong>, <em>, <code>, <a href=''>, <br>, etc."
                  rows={6}
                />
                <div className="list-hint">
                  💡 Cada línea = 1 ítem. Puedes usar HTML inline para formato.
                </div>
              </>
            )}

            {section.type === 'table' && (
              <>
                <div className="table-info">
                  <p>Puedes pegar el HTML de una tabla o editarla manualmente:</p>
                </div>
                <textarea
                  value={section.content}
                  onChange={(e) => handleSectionChange(index, 'content', e.target.value)}
                  placeholder="<table>&#10;  <tr>&#10;    <th>Columna 1</th>&#10;    <th>Columna 2</th>&#10;  </tr>&#10;  <tr>&#10;    <td>Dato 1</td>&#10;    <td>Dato 2</td>&#10;  </tr>&#10;</table>"
                  rows={8}
                  className="table-textarea"
                />
                {section.content && (
                  <div 
                    className="table-preview"
                    dangerouslySetInnerHTML={{ __html: section.content }}
                  />
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

// Editor HTML
const HTMLEditor = ({ htmlContent, onChange, onApply }) => {
  return (
    <div className="html-editor">
      <div className="html-editor-header">
        <label>Código HTML del Artículo</label>
        <button type="button" className="btn-apply-html" onClick={onApply}>
          ✓ Aplicar Cambios
        </button>
      </div>
      <div className="html-editor-info">
        <p>
          <strong>✅ Etiquetas soportadas:</strong> Todo HTML5 estándar incluyendo{' '}
          <code>&lt;p&gt;</code>, <code>&lt;h1-h6&gt;</code>, <code>&lt;div&gt;</code>, <code>&lt;section&gt;</code>,{' '}
          <code>&lt;blockquote&gt;</code>, <code>&lt;figure&gt;</code>, <code>&lt;img&gt;</code>,{' '}
          <code>&lt;ul&gt;</code>, <code>&lt;ol&gt;</code>, <code>&lt;li&gt;</code>,{' '}
          <code>&lt;table&gt;</code>, <code>&lt;tr&gt;</code>, <code>&lt;td&gt;</code>, <code>&lt;th&gt;</code>,{' '}
          <code>&lt;strong&gt;</code>, <code>&lt;em&gt;</code>, <code>&lt;a&gt;</code>, <code>&lt;br&gt;</code>,{' '}
          <code>&lt;span&gt;</code>, <code>&lt;code&gt;</code>, <code>&lt;pre&gt;</code>, <code>&lt;hr&gt;</code>, y más.
        </p>
      </div>
      <textarea
        className="html-textarea"
        value={htmlContent}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`<!-- Pega aquí tu HTML completo -->
<h1>Título Principal</h1>

<p>Este es un párrafo con <strong>texto en negrita</strong>, <em>cursiva</em>, y <a href="https://ejemplo.com">un enlace</a>.</p>

<h2>Subtítulo</h2>

<blockquote>
  Esta es una cita importante
  <cite>— Autor</cite>
</blockquote>

<ul>
  <li>Item 1 con <strong>formato</strong></li>
  <li>Item 2 con <code>código</code></li>
  <li>Item 3 con <a href="#">enlace</a></li>
</ul>

<table>
  <tr>
    <th>Columna 1</th>
    <th>Columna 2</th>
  </tr>
  <tr>
    <td>Dato 1</td>
    <td>Dato 2</td>
  </tr>
</table>

<figure>
  <img src='https://ejemplo.com/imagen.jpg' alt='Descripción' />
  <figcaption>Pie de foto</figcaption>
</figure>

<hr />`}
        rows={25}
        spellCheck={false}
      />
      <div className="html-editor-hint">
        <strong>💡 Tip:</strong> Haz clic en "✓ Aplicar Cambios" para convertir tu HTML a secciones visuales.
        El parser detecta automáticamente todos los elementos y preserva el formato completo.
      </div>
    </div>
  );
};

// ==================== PREVIEW DEL ARTÍCULO ====================

const ArticlePreview = ({ formData, templateContent, templateType, onClose, onConfirmSave }) => {
  const renderPreviewContent = () => {
    switch (templateType) {
      case 'multiNews':
        return (
          <div className="preview-multi-news">
            {templateContent.map((news, index) => (
              news.title && news.body && (
                <article key={index} className="preview-news-item">
                  <h3>{news.title}</h3>
                  {news.imageUrl && (
                    <img src={news.imageUrl} alt={news.title} className={`preview-img-${news.imageSize}`} />
                  )}
                  <p>{news.body}</p>
                  {news.url && (
                    <a href={news.url} target="_blank" rel="noopener noreferrer">
                      {news.urlText || 'Leer más'}
                    </a>
                  )}
                </article>
              )
            ))}
          </div>
        );

      case 'longForm':
        return (
          <div className="preview-longform">
            {templateContent.sections.map((section, index) => {
              switch (section.type) {
                case 'paragraph':
                  return <div key={index} dangerouslySetInnerHTML={{ __html: section.content }} />;
                case 'heading':
                  return <h2 key={index}>{section.content}</h2>;
                case 'quote':
                  return (
                    <blockquote key={index}>
                      <div dangerouslySetInnerHTML={{ __html: section.content }} />
                      {section.author && <cite>— {section.author}</cite>}
                    </blockquote>
                  );
                case 'image':
                  return (
                    <figure key={index}>
                      <img src={section.imageUrl} alt={section.imageCaption || ''} />
                      {section.imageCaption && <figcaption>{section.imageCaption}</figcaption>}
                    </figure>
                  );
                case 'list':
                  return (
                    <ul key={index}>
                      {section.content.split('\n').filter(item => item.trim()).map((item, i) => (
                        <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
                      ))}
                    </ul>
                  );
                case 'table':
                  return <div key={index} dangerouslySetInnerHTML={{ __html: section.content }} />;
                default:
                  return null;
              }
            })}
          </div>
        );

      case 'tutorial':
        return (
          <div className="preview-tutorial">
            {templateContent.steps.map((step, index) => (
              <div key={index} className="preview-tutorial-step">
                <div className="step-number">Paso {index + 1}</div>
                <h3>{step.title}</h3>
                <p>{step.content}</p>
                {step.imageUrl && <img src={step.imageUrl} alt={step.title} />}
              </div>
            ))}
          </div>
        );

      case 'analysis':
        return (
          <div className="preview-analysis">
            {templateContent.introduction && (
              <div className="analysis-intro">
                <p>{templateContent.introduction}</p>
              </div>
            )}
            {templateContent.keyPoints.length > 0 && (
              <div className="analysis-keypoints">
                <h3>Puntos Clave</h3>
                {templateContent.keyPoints.map((point, index) => (
                  <div key={index} className="keypoint">
                    <h4>💡 {point.title}</h4>
                    <p>{point.content}</p>
                  </div>
                ))}
              </div>
            )}
            {templateContent.conclusion && (
              <div className="analysis-conclusion">
                <h3>Conclusión</h3>
                <p>{templateContent.conclusion}</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="article-preview-modal">
      <div className="preview-overlay" onClick={onClose}></div>
      <div className="preview-container">
        <div className="preview-header">
          <h2>Preview del Artículo</h2>
          <button className="preview-close" onClick={onClose}>×</button>
        </div>

        <div className="preview-content">
          {formData.bannerUrl && (
            <div className="preview-banner">
              <img src={formData.bannerUrl} alt={formData.title} />
            </div>
          )}

          <div className="preview-meta">
            <h1>{formData.title}</h1>
            {formData.excerpt && <p className="preview-excerpt">{formData.excerpt}</p>}
            <div className="preview-info">
              <span>👤 {formData.authorName}</span>
              <span>📅 {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              <span className={`preview-status status-${formData.status}`}>
                {formData.status === 'published' ? '✓ Publicado' : '📝 Borrador'}
              </span>
            </div>
          </div>

          <div className="preview-body">
            {renderPreviewContent()}
          </div>
        </div>

        <div className="preview-actions">
          <button className="btn btn--secondary" onClick={onClose}>
            ← Volver a Editar
          </button>
          <button className="btn btn--primary" onClick={onConfirmSave}>
            ✓ Confirmar y Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

// Editor Tutorial/Guía
const TutorialEditor = ({ content, onChange }) => {
  const handleStepChange = (index, field, value) => {
    const newSteps = [...content.steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    onChange({ ...content, steps: newSteps });
  };

  const handleAddStep = () => {
    onChange({ ...content, steps: [...content.steps, { title: '', content: '', imageUrl: '' }] });
  };

  const handleRemoveStep = (index) => {
    if (content.steps.length > 1) {
      onChange({ ...content, steps: content.steps.filter((_, i) => i !== index) });
    }
  };

  const handleMoveStep = (index, direction) => {
    const newSteps = [...content.steps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
    onChange({ ...content, steps: newSteps });
  };

  return (
    <div className="tutorial-editor">
      <div className="tutorial-header">
        <label>Pasos del Tutorial</label>
        <button type="button" onClick={handleAddStep}>
          <Plus size={16} />
          Añadir Paso
        </button>
      </div>

      <div className="tutorial-steps">
        {content.steps.map((step, index) => (
          <div key={index} className="tutorial-step">
            <div className="step-header">
              <span className="step-number">Paso {index + 1}</span>
              <div className="step-actions">
                {index > 0 && (
                  <button type="button" onClick={() => handleMoveStep(index, 'up')}>↑</button>
                )}
                {index < content.steps.length - 1 && (
                  <button type="button" onClick={() => handleMoveStep(index, 'down')}>↓</button>
                )}
                {content.steps.length > 1 && (
                  <button type="button" onClick={() => handleRemoveStep(index)}>
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>

            <input
              type="text"
              value={step.title}
              onChange={(e) => handleStepChange(index, 'title', e.target.value)}
              placeholder={`Título del paso ${index + 1}`}
            />

            <textarea
              value={step.content}
              onChange={(e) => handleStepChange(index, 'content', e.target.value)}
              placeholder="Descripción detallada del paso..."
              rows={4}
            />

            <input
              type="url"
              value={step.imageUrl}
              onChange={(e) => handleStepChange(index, 'imageUrl', e.target.value)}
              placeholder="URL de imagen (opcional)"
            />

            <ImageUploader onImageUploaded={(url) => handleStepChange(index, 'imageUrl', url)} />

            {step.imageUrl && (
              <img src={step.imageUrl} alt={`Step ${index + 1}`} onError={(e) => e.target.style.display = 'none'} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Editor Análisis/Insight
const AnalysisEditor = ({ content, onChange }) => {
  const handleKeyPointChange = (index, field, value) => {
    const newKeyPoints = [...content.keyPoints];
    newKeyPoints[index] = { ...newKeyPoints[index], [field]: value };
    onChange({ ...content, keyPoints: newKeyPoints });
  };

  const handleAddKeyPoint = () => {
    onChange({ ...content, keyPoints: [...content.keyPoints, { title: '', content: '' }] });
  };

  const handleRemoveKeyPoint = (index) => {
    if (content.keyPoints.length > 1) {
      onChange({ ...content, keyPoints: content.keyPoints.filter((_, i) => i !== index) });
    }
  };

  return (
    <div className="analysis-editor">
      <div className="form-group">
        <label>Introducción</label>
        <textarea
          value={content.introduction}
          onChange={(e) => onChange({ ...content, introduction: e.target.value })}
          placeholder="Introduce el análisis con contexto general..."
          rows={4}
        />
      </div>

      <div className="keypoints-section">
        <div className="keypoints-header">
          <label>Puntos Clave</label>
          <button type="button" onClick={handleAddKeyPoint}>
            <Plus size={16} />
            Añadir Punto Clave
          </button>
        </div>

        {content.keyPoints.map((point, index) => (
          <div key={index} className="keypoint-item">
            <div className="keypoint-header">
              <span className="keypoint-number">💡 Punto {index + 1}</span>
              {content.keyPoints.length > 1 && (
                <button type="button" onClick={() => handleRemoveKeyPoint(index)}>
                  <Trash2 size={16} />
                </button>
              )}
            </div>

            <input
              type="text"
              value={point.title}
              onChange={(e) => handleKeyPointChange(index, 'title', e.target.value)}
              placeholder="Título del punto clave"
            />

            <textarea
              value={point.content}
              onChange={(e) => handleKeyPointChange(index, 'content', e.target.value)}
              placeholder="Desarrolla este punto..."
              rows={3}
            />
          </div>
        ))}
      </div>

      <div className="form-group">
        <label>Conclusión</label>
        <textarea
          value={content.conclusion}
          onChange={(e) => onChange({ ...content, conclusion: e.target.value })}
          placeholder="Resume las conclusiones principales del análisis..."
          rows={4}
        />
      </div>
    </div>
  );
};

// Formulario de edición de post
const PostForm = ({ post, onSave, onCancel, loading, user }) => {
  const detectTemplate = (post) => {
    if (!post) return 'multiNews';
    if (post.templateType) return post.templateType;

    if (post.news1Title) return 'multiNews';

    if (post.templateContent) {
      const content = typeof post.templateContent === 'string'
        ? JSON.parse(post.templateContent)
        : post.templateContent;

      if (content.sections) return 'longForm';
      if (content.steps) return 'tutorial';
      if (content.keyPoints) return 'analysis';
    }

    return 'multiNews';
  };

  const parseContent = (post, template) => {
    if (!post) {
      return ARTICLE_TEMPLATES[template].defaultContent();
    }

    if (post.templateContent) {
      try {
        return typeof post.templateContent === 'string'
          ? JSON.parse(post.templateContent)
          : post.templateContent;
      } catch (e) {
        console.error('Error parsing templateContent:', e);
        return ARTICLE_TEMPLATES[template].defaultContent();
      }
    }

    if (template === 'multiNews') {
      const newsArray = [];
      for (let i = 1; i <= 5; i++) {
        const title = post[`news${i}Title`];
        const body = post[`news${i}Body`];

        if (title || body) {
          newsArray.push({
            title: title || '',
            body: body || '',
            url: post[`news${i}Url`] || '',
            urlText: post[`news${i}UrlText`] || '',
            imageUrl: post[`news${i}ImageUrl`] || '',
            imageSize: post[`news${i}ImageSize`] || 'medium',
          });
        }
      }
      return newsArray.length > 0 ? newsArray : ARTICLE_TEMPLATES[template].defaultContent();
    }

    return ARTICLE_TEMPLATES[template].defaultContent();
  };

  const [selectedTemplate, setSelectedTemplate] = useState(detectTemplate(post));
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState({
    title: post?.title || getDefaultTitle(),
    excerpt: post?.excerpt || '',
    bannerUrl: post?.bannerUrl || '',
    authorName: post?.authorName || user?.username || '',
    language: post?.language || 'en',
    status: post?.status || 'draft',
    featured: post?.featured || false,
  });

  const [seoData, setSeoData] = useState({
    metaTitle: post?.metaTitle || '',
    metaDescription: post?.metaDescription || '',
    ogTitle: post?.ogTitle || '',
    ogDescription: post?.ogDescription || '',
    ogImage: post?.ogImage || '',
    twitterCard: post?.twitterCard || 'summary_large_image',
  });

  const [templateContent, setTemplateContent] = useState(parseContent(post, selectedTemplate));

  const handleTemplateChange = (newTemplate) => {
    if (window.confirm('¿Cambiar de plantilla? Se perderá el contenido actual.')) {
      setSelectedTemplate(newTemplate);
      setTemplateContent(ARTICLE_TEMPLATES[newTemplate].defaultContent());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let isValid = false;

    if (selectedTemplate === 'multiNews') {
      isValid = templateContent.some(item => item.title && item.body);
      if (!isValid) {
        alert('Debes completar al menos el título y cuerpo de una noticia');
        return;
      }
    } else if (selectedTemplate === 'longForm') {
      isValid = templateContent.sections.some(s => s.content);
      if (!isValid) {
        alert('Debes completar al menos una sección del artículo');
        return;
      }
    } else if (selectedTemplate === 'tutorial') {
      isValid = templateContent.steps.some(s => s.title && s.content);
      if (!isValid) {
        alert('Debes completar al menos un paso del tutorial');
        return;
      }
    } else if (selectedTemplate === 'analysis') {
      isValid = templateContent.introduction || templateContent.conclusion ||
        templateContent.keyPoints.some(p => p.title && p.content);
      if (!isValid) {
        alert('Debes completar al menos la introducción, un punto clave o la conclusión');
        return;
      }
    }

    // Mostrar preview en lugar de guardar directamente
    setShowPreview(true);
  };

  const handleConfirmSave = async () => {
    const isPublished = formData.status === 'published';

    const dataToSave = {
      ...formData,
      ...seoData,
      published: isPublished,
      status: isPublished ? 'published' : 'draft',
      templateType: selectedTemplate,
      templateContent: JSON.stringify(templateContent),
    };

    if (selectedTemplate === 'multiNews') {
      templateContent.forEach((news, index) => {
        const newsNum = index + 1;
        dataToSave[`news${newsNum}Title`] = news.title || null;
        dataToSave[`news${newsNum}Body`] = news.body || null;
        dataToSave[`news${newsNum}Url`] = news.url || null;
        dataToSave[`news${newsNum}UrlText`] = news.urlText || null;
        dataToSave[`news${newsNum}ImageUrl`] = news.imageUrl || null;
        dataToSave[`news${newsNum}ImageSize`] = news.imageSize || 'medium';
      });
    }

    setShowPreview(false);
    await onSave(dataToSave);
  };

  const handleImageError = (e) => {
    e.target.style.display = 'none';
  };

  const renderContentEditor = () => {
    switch (selectedTemplate) {
      case 'multiNews':
        return <MultiNewsEditor content={templateContent} onChange={setTemplateContent} />;
      case 'longForm':
        return <LongFormEditor content={templateContent} onChange={setTemplateContent} />;
      case 'tutorial':
        return <TutorialEditor content={templateContent} onChange={setTemplateContent} />;
      case 'analysis':
        return <AnalysisEditor content={templateContent} onChange={setTemplateContent} />;
      default:
        return null;
    }
  };

  return (
    <>
      <form className="post-form" onSubmit={handleSubmit}>
        <div className="form-header">
          <h2>{post ? 'Editar Artículo' : 'Nuevo Artículo'}</h2>
          <div className="form-actions">
            <button type="button" className="btn btn--secondary" onClick={onCancel}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={loading}
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>

        <div className="form-grid">
          <div className="form-main">
            <div className="form-group">
              <label htmlFor="title">Título *</label>
              <input
                id="title"
                type="text"
                value={formData.title}
                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Título del artículo"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="excerpt">Extracto</label>
              <textarea
                id="excerpt"
                value={formData.excerpt}
                onChange={e => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                placeholder="Breve descripción del artículo (máx. 500 caracteres)"
                maxLength={500}
                rows={3}
              />
              <span className="char-count">{formData.excerpt.length}/500</span>
            </div>

            <SEOEditor seoData={seoData} onChange={setSeoData} />

            <div className="template-section">
              <TemplateSelector
                selectedTemplate={selectedTemplate}
                onSelect={handleTemplateChange}
              />
            </div>

            <div className="content-editor-section">
              {renderContentEditor()}
            </div>
          </div>

          <div className="form-sidebar">
            <div className="form-group">
              <label htmlFor="bannerUrl">URL del Banner</label>
              <input
                id="bannerUrl"
                type="url"
                value={formData.bannerUrl}
                onChange={e => setFormData(prev => ({ ...prev, bannerUrl: e.target.value }))}
                placeholder="https://ejemplo.com/imagen.jpg"
              />
              <ImageUploader onImageUploaded={(url) => setFormData(prev => ({ ...prev, bannerUrl: url }))} />
              {formData.bannerUrl && (
                <div className="banner-preview">
                  <img
                    src={formData.bannerUrl}
                    alt="Preview"
                    onError={handleImageError}
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="authorName">Autor</label>
              <input
                id="authorName"
                type="text"
                value={formData.authorName}
                readOnly
                disabled
                placeholder="Nombre del autor"
                style={{
                  cursor: 'not-allowed',
                  opacity: 0.7,
                  backgroundColor: 'var(--card-bg, #f3f4f6)'
                }}
              />
              <span className="label-hint" style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                El autor se asigna automáticamente según tu usuario
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="language">Idioma *</label>
              <select
                id="language"
                value={formData.language}
                onChange={e => setFormData(prev => ({ ...prev, language: e.target.value }))}
                required
              >
                <option value="en">English (ENG)</option>
                <option value="es">Español (ESP)</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Estado</label>
              <select
                id="status"
                value={formData.status}
                onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="draft">Borrador</option>
                <option value="published">Publicado</option>
              </select>
            </div>

            <div className="form-group form-group--checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={e => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                />
                <span>Artículo destacado</span>
              </label>
            </div>
          </div>
        </div>
      </form>

      {showPreview && (
        <ArticlePreview
          formData={formData}
          templateContent={templateContent}
          templateType={selectedTemplate}
          onClose={() => setShowPreview(false)}
          onConfirmSave={handleConfirmSave}
        />
      )}
    </>
  );
};

// Componente principal del Editor
const WhataNewsEdit = () => {
  const { theme, toggleTheme } = useTheme();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isBilingualMode, setIsBilingualMode] = useState(false);
  const [filter, setFilter] = useState('all');

  const handleLogout = () => {
    logout();
    navigate('/news-login');
  };

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const statusParam = filter === 'all' ? '' : `&status=${filter}`;
      const [postsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/blog?limit=100${statusParam}`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${API_BASE}/blog/stats`, {
          headers: getAuthHeaders(),
        }),
      ]);

      if (!postsRes.ok || !statsRes.ok) {
        if (postsRes.status === 401 || statsRes.status === 401) {
          logout();
          navigate('/news-login');
          return;
        }
        throw new Error('Error al cargar datos');
      }

      const postsData = await postsRes.json();
      const statsData = await statsRes.json();

      setPosts(postsData.posts);
      setStats(statsData);
    } catch (err) {
      setError(err.message || 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, [filter, logout, navigate]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleSave = async (data) => {
    try {
      setSaving(true);
      setError(null);

      const url = editingPost
        ? `${API_BASE}/blog/${editingPost.id}`
        : `${API_BASE}/blog`;

      const method = editingPost ? 'PATCH' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        if (res.status === 401) {
          logout();
          navigate('/news-login');
          return;
        }
        const errorData = await res.json();
        throw new Error(errorData.message || 'Error al guardar');
      }

      await fetchPosts();
      setEditingPost(null);
      setIsCreating(false);
    } catch (err) {
      console.error('Error en handleSave:', err);
      setError(err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Función para guardar posts bilingües (ESP + ENG)
  const handleBilingualSave = async ({ spanishPost, englishPost }) => {
    try {
      setSaving(true);
      setError(null);

      // Función para limpiar campos no aceptados por el backend
      const cleanPostData = (post) => {
        const allowedFields = [
          'title', 'excerpt', 'bannerUrl', 'authorName', 'language', 'status',
          'published', 'featured', 'templateType', 'templateContent',
          'metaTitle', 'metaDescription', 'ogTitle', 'ogDescription', 'ogImage', 'twitterCard',
          'news1Title', 'news1Body', 'news1Url', 'news1UrlText', 'news1ImageUrl', 'news1ImageSize',
          'news2Title', 'news2Body', 'news2Url', 'news2UrlText', 'news2ImageUrl', 'news2ImageSize',
          'news3Title', 'news3Body', 'news3Url', 'news3UrlText', 'news3ImageUrl', 'news3ImageSize',
          'news4Title', 'news4Body', 'news4Url', 'news4UrlText', 'news4ImageUrl', 'news4ImageSize',
          'news5Title', 'news5Body', 'news5Url', 'news5UrlText', 'news5ImageUrl', 'news5ImageSize',
        ];
        const cleaned = {};
        for (const key of allowedFields) {
          if (post[key] !== undefined && post[key] !== null && post[key] !== '') {
            cleaned[key] = post[key];
          }
        }
        return cleaned;
      };

      // Limpiar los posts antes de enviar
      const cleanedSpanish = cleanPostData(spanishPost);
      const cleanedEnglish = cleanPostData(englishPost);

      console.log('[Bilingual] Sending Spanish:', cleanedSpanish);
      console.log('[Bilingual] Sending English:', cleanedEnglish);

      // Guardar post en español
      const esRes = await fetch(`${API_BASE}/blog`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(cleanedSpanish),
      });

      if (!esRes.ok) {
        if (esRes.status === 401) {
          logout();
          navigate('/news-login');
          return;
        }
        const errorData = await esRes.json();
        throw new Error(errorData.message || 'Error al guardar versión española');
      }

      const savedSpanish = await esRes.json();
      console.log('[Bilingual] Spanish post saved:', savedSpanish.id);

      // Guardar post en inglés
      const enRes = await fetch(`${API_BASE}/blog`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(cleanedEnglish),
      });

      if (!enRes.ok) {
        if (enRes.status === 401) {
          logout();
          navigate('/news-login');
          return;
        }
        const errorData = await enRes.json();
        throw new Error(errorData.message || 'Error al guardar versión inglesa');
      }

      const savedEnglish = await enRes.json();
      console.log('[Bilingual] English post saved:', savedEnglish.id);

      // Éxito - refrescar lista y cerrar editor
      await fetchPosts();
      setIsBilingualMode(false);
      setIsCreating(false);
      
      // Mostrar mensaje de éxito
      alert(`✅ Posts bilingües creados exitosamente!\n\n🇪🇸 Español: ${spanishPost.title}\n🇬🇧 English: ${englishPost.title}`);
      
    } catch (err) {
      console.error('Error en handleBilingualSave:', err);
      setError(err.message || 'Error al guardar posts bilingües');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este artículo?')) return;

    try {
      const res = await fetch(`${API_BASE}/blog/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        if (res.status === 401) {
          logout();
          navigate('/news-login');
          return;
        }
        throw new Error('Error al eliminar');
      }

      await fetchPosts();
    } catch (err) {
      setError(err.message || 'Error al eliminar');
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/blog/${id}`, {
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        if (res.status === 401) {
          logout();
          navigate('/news-login');
          return;
        }
        throw new Error('Error al cargar post');
      }

      const post = await res.json();
      setEditingPost(post);
      setIsCreating(false);
    } catch (err) {
      setError(err.message || 'Error al cargar');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTemplateLabel = (post) => {
    const templateType = post.templateType || 'multiNews';
    const template = ARTICLE_TEMPLATES[templateType];
    return template ? template.name : 'Multi-Noticias';
  };

  // ========== MODO BILINGÜE ==========
  if (isBilingualMode) {
    return (
      <div className="whatablog-edit" data-theme={theme}>
        <header className="edit-header">
          <div className="edit-header-content">
            <Link to="/" className="edit-logo">
              <img src="/logo.svg" alt="WhataLab" />
              <span>WhataLab Editor</span>
            </Link>
            <button className="theme-toggle" onClick={toggleTheme}>
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
          </div>
        </header>
        <main className="edit-main" style={{ paddingTop: '80px' }}>
          <BilingualNewsEditor
            onSave={handleBilingualSave}
            onCancel={() => {
              setIsBilingualMode(false);
            }}
            loading={saving}
            user={user}
            apiBase={API_BASE}
          />
        </main>
      </div>
    );
  }

  if (isCreating || editingPost) {
    return (
      <div className="whatablog-edit" data-theme={theme}>
        <header className="edit-header">
          <div className="edit-header-content">
            <Link to="/" className="edit-logo">
              <img src="/logo.svg" alt="WhataLab" />
              <span>WhataLab Editor</span>
            </Link>
            <button className="theme-toggle" onClick={toggleTheme}>
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
          </div>
        </header>
        <main className="edit-main">
          <div className="edit-container">
            {error && (
              <div className="edit-error">
                <p>{error}</p>
                <button onClick={() => setError(null)}>×</button>
              </div>
            )}
            <PostForm
              post={editingPost}
              onSave={handleSave}
              onCancel={() => {
                setEditingPost(null);
                setIsCreating(false);
              }}
              loading={saving}
              user={user}
            />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="whatablog-edit" data-theme={theme}>
      <header className="edit-header">
        <div className="edit-header-content">
          <Link to="/" className="edit-logo">
            <img src="/logo.svg" alt="WhataLab" />
            <span>WhataLab Editor</span>
          </Link>
          <nav className="edit-nav">
            {user && <span className="edit-user">👤 {user.username}</span>}
            <Link to="/whatanews" className="edit-nav-link">Ver Blog</Link>
            <button className="theme-toggle" onClick={toggleTheme}>
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <button className="btn-logout" onClick={handleLogout} title="Cerrar sesión">
              <LogOut size={18} />
            </button>
          </nav>
        </div>
      </header>

      <main className="edit-main">
        <div className="edit-container">
          {stats && (
            <section className="edit-stats">
              <div className="stat-card">
                <span className="stat-value">{stats.total}</span>
                <span className="stat-label">Total</span>
              </div>
              <div className="stat-card stat-card--published">
                <span className="stat-value">{stats.published}</span>
                <span className="stat-label">Publicados</span>
              </div>
              <div className="stat-card stat-card--draft">
                <span className="stat-value">{stats.drafts}</span>
                <span className="stat-label">Borradores</span>
              </div>
              <div className="stat-card stat-card--views">
                <span className="stat-value">{stats.totalViews.toLocaleString()}</span>
                <span className="stat-label">Vistas Totales</span>
              </div>
            </section>
          )}

          <section className="edit-actions">
            <div className="edit-filters">
              <button
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                Todos
              </button>
              <button
                className={`filter-btn ${filter === 'published' ? 'active' : ''}`}
                onClick={() => setFilter('published')}
              >
                Publicados
              </button>
              <button
                className={`filter-btn ${filter === 'draft' ? 'active' : ''}`}
                onClick={() => setFilter('draft')}
              >
                Borradores
              </button>
            </div>
            <div className="edit-actions-buttons">
              <button className="btn btn--primary" onClick={() => setIsCreating(true)}>
                + Nuevo Artículo
              </button>
              <button 
                className="btn btn--bilingual" 
                onClick={() => setIsBilingualMode(true)}
                title="Crear Multi-Noticias en Español e Inglés con traducción automática"
              >
                <Languages size={18} />
                Multi-Noticias Bilingüe
              </button>
            </div>
          </section>

          {error && (
            <div className="edit-error">
              <p>{error}</p>
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          {loading ? (
            <div className="edit-loading">
              <div className="edit-loading-spinner"></div>
              <p>Cargando artículos...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="edit-empty">
              <h3>No hay artículos</h3>
              <p>Crea tu primer artículo para comenzar.</p>
              <button className="btn btn--primary" onClick={() => setIsCreating(true)}>
                Crear Artículo
              </button>
            </div>
          ) : (
            <section className="posts-table">
              <table>
                <thead>
                  <tr>
                    <th>Título</th>
                    <th>Plantilla</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                    <th>Vistas</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map(post => (
                    <tr key={post.id}>
                      <td className="post-title-cell">
                        <div className="post-title-wrapper">
                          {post.bannerUrl && (
                            <img src={post.bannerUrl} alt="" className="post-thumb" />
                          )}
                          <div>
                            <strong>{post.title}</strong>
                            {post.featured && <span className="featured-badge">★</span>}
                            <span className="post-slug">/{post.slug}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="template-badge">
                          {getTemplateLabel(post)}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge status-${post.status}`}>
                          {post.status === 'published' ? 'Publicado' : 'Borrador'}
                        </span>
                      </td>
                      <td>{formatDate(post.publishedAt || post.createdAt)}</td>
                      <td>{post.views}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="action-btn action-btn--edit"
                            onClick={() => handleEdit(post.id)}
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            className="action-btn action-btn--delete"
                            onClick={() => handleDelete(post.id)}
                            title="Eliminar"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>
      </main>
    </div>
  );
};

export default WhataNewsEdit;